// Plaid /transactions/sync JSON loader. Mirrors scripts/loaders.py::load_plaid_json.
//
// The doc shape (one file per sync call) is:
//   {
//     "item_id": "item_abc",
//     "institution_name": "BMO",
//     "accounts": [{account_id, name, mask, subtype, ...}],
//     "added":    [{transaction_id, account_id, date, amount, name, merchant_name, ...}],
//     "modified": [...],
//     "removed":  [{transaction_id}, ...]
//   }
//
// Sign convention: Plaid uses positive = outflow (expense) — already
// matches our convention. No flip.
//
// The desktop's Rust layer writes these JSON files to raw/<YYYY-MM>/
// after each /transactions/sync call. The web app can ingest the same
// files via the existing folder picker, no extra UI needed for v1.

import {
  type Transaction,
  newTransaction,
  makeId,
  normalizeVendor,
  isInterPortalTransfer,
  extractLast4,
} from "../common.js";

interface PlaidAccount {
  account_id?: string;
  name?: string;
  mask?: string;
  subtype?: string;
}

interface PlaidTx {
  transaction_id?: string;
  account_id?: string;
  date?: string;
  amount?: number | string;
  name?: string;
  merchant_name?: string;
  iso_currency_code?: string;
  pending?: boolean;
}

interface PlaidDoc {
  item_id?: string;
  institution_name?: string;
  accounts?: PlaidAccount[];
  added?: PlaidTx[];
  modified?: PlaidTx[];
  removed?: Array<{ transaction_id?: string }>;
}

function plaidAccountLabel(account: PlaidAccount): string {
  const name = (account.name ?? "").trim() || "Account";
  const mask = (account.mask ?? "").trim();
  return mask ? `${name} ****${mask}` : name;
}

async function plaidTxToTransaction(
  tx: PlaidTx,
  accountLabel: string,
  accountLast4: string,
  institutionName: string,
  sourceFile: string,
): Promise<Transaction | null> {
  const date = (tx.date ?? "").trim();
  if (!date) return null;
  const amount = Number(tx.amount ?? 0);
  if (!Number.isFinite(amount)) return null;
  const description = (tx.merchant_name ?? tx.name ?? "").trim();
  if (!description || isInterPortalTransfer(description)) return null;

  const id = await makeId("plaid", accountLabel, date, amount, description);
  return newTransaction({
    id,
    date,
    vendor_raw: description,
    vendor_key: normalizeVendor(description),
    amount: Math.round(amount * 100) / 100,
    portal: "plaid",
    account: accountLabel,
    // Aggregator-sourced — assume a depository account by default. Credit
    // cards work fine with this too since Plaid already ships our sign
    // convention; account_kind is used by loaders for SIGN flipping, not
    // by the dashboard for display, so "debit" is safe here.
    account_kind: "debit",
    account_last4: accountLast4,
    source_file: sourceFile,
    raw: {
      plaid_transaction_id: tx.transaction_id ?? "",
      plaid_account_id: tx.account_id ?? "",
      currency: tx.iso_currency_code ?? "",
      pending: tx.pending ?? false,
    },
    institution_name: institutionName,
  });
}

export async function loadPlaidJson(
  text: string,
  filename: string,
): Promise<Transaction[]> {
  let doc: PlaidDoc;
  try {
    doc = JSON.parse(text);
  } catch (err) {
    throw new Error(`Plaid JSON parse error in ${filename}: ${(err as Error).message}`);
  }
  const accounts = doc.accounts ?? [];
  const labels = new Map<string, string>();
  const masks = new Map<string, string>();
  for (const a of accounts) {
    const aid = a.account_id ?? "";
    if (!aid) continue;
    labels.set(aid, plaidAccountLabel(a));
    masks.set(aid, extractLast4(a.mask ?? ""));
  }
  const institutionName = (doc.institution_name ?? "").trim();
  const all: PlaidTx[] = [...(doc.added ?? []), ...(doc.modified ?? [])];
  const out: Transaction[] = [];
  for (const tx of all) {
    const aid = tx.account_id ?? "";
    const label = labels.get(aid) ?? "Account";
    const last4 = masks.get(aid) ?? "";
    const t = await plaidTxToTransaction(tx, label, last4, institutionName, filename);
    if (t) out.push(t);
  }
  return out;
}

/**
 * Returns the set of Plaid transaction_ids the doc marks as `removed`.
 * Caller (state.ts when we wire this) iterates the in-memory bundle and
 * marks any tx whose raw.plaid_transaction_id is in the set as deleted.
 * Mirrors apply_plaid_removed in scripts/loaders.py — Phase B work; v1
 * just returns the set without applying it.
 */
export function readPlaidRemovedIds(text: string): Set<string> {
  let doc: PlaidDoc;
  try {
    doc = JSON.parse(text);
  } catch {
    return new Set();
  }
  const ids = new Set<string>();
  for (const r of doc.removed ?? []) {
    if (r.transaction_id) ids.add(r.transaction_id);
  }
  return ids;
}

export const _internal = { plaidAccountLabel, plaidTxToTransaction };
